
import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { X, Plus, Pencil, Trash2, Loader2, Image as ImageIcon, Upload } from "lucide-react";
import { useIsMobile } from '@/hooks/use-mobile';
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/use-toast';
import ImageSearchModal from '@/components/admin/ImageSearchModal';
import ImageUploadModal from '@/components/admin/ImageUploadModal';
import FreepikIconSearchModal from '@/components/admin/FreepikIconSearchModal';

// Function to generate a translation key from a name
const generateTranslationKey = (prefix: string, name: string): string => {
  return `${prefix}.${name.toLowerCase().replace(/\s+/g, '_')}`;
};

const CategoryManagement = () => {
  const isMobile = useIsMobile();
  const [isAddingCategoryGroup, setIsAddingCategoryGroup] = useState(false);
  const [newCategoryGroup, setNewCategoryGroup] = useState({ name: "", description: "" });
  const [isAddingCategoryDialog, setIsAddingCategoryDialog] = useState(false);
  const [newCategory, setNewCategory] = useState({ name: "", description: "", category_group_id: "", business_banner_image_url: "" });
  const [isEditingCategory, setIsEditingCategory] = useState(false);
  const [editingCategory, setEditingCategory] = useState({ id: "", name: "", description: "", category_group_id: "", translation_key: "", business_banner_image_url: "" });
  const [isImageSearchOpen, setIsImageSearchOpen] = useState(false);
  const [isEditImageSearchOpen, setIsEditImageSearchOpen] = useState(false);
  const [isImageUploadOpen, setIsImageUploadOpen] = useState(false);
  const [isEditImageUploadOpen, setIsEditImageUploadOpen] = useState(false);
  const [isDeletingCategory, setIsDeletingCategory] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState("");
  const [isAddingGroup, setIsAddingGroup] = useState(false);
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [isUpdatingCategory, setIsUpdatingCategory] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isIconSearchOpen, setIsIconSearchOpen] = useState(false);
  const [selectedCategoryGroupForIcon, setSelectedCategoryGroupForIcon] = useState<{id: string; name: string; translation_key: string} | null>(null);

  const queryClient = useQueryClient();

  // Fetch category groups with their translated names
  const { data: categoryGroupsData, isLoading: categoryGroupsLoading } = useQuery({
    queryKey: ['adminCategoryGroups'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('category_groups')
        .select(`
          id,
          description,
          translation_key,
          icon_url,
          translation_defaults!inner(key, default_value)
        `);

      if (error) throw error;

      return data.map(group => ({
        ...group,
        name: group.translation_defaults.default_value,
        icon_url: group.icon_url || null
      }));
    },
  });

  // Fetch categories with their translated names
  const { data: categoriesData, isLoading: categoriesLoading } = useQuery({
    queryKey: ['adminCategories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('categories')
        .select(`
          id, 
          description, 
          category_group_id, 
          translation_key,
          business_banner_image_url,
          translation_defaults!inner(key, default_value)
        `);
      
      if (error) throw error;
      
      return data.map(category => ({
        ...category,
        name: category.translation_defaults.default_value,
        business_banner_image_url: category.business_banner_image_url || ""
      }));
    },
  });

  // Update a category with translation support
  const updateCategoryMutation = useMutation({
    mutationFn: async (category: { id: string; name: string; description: string; category_group_id: string; translation_key: string; business_banner_image_url: string }) => {
      // First, update the default translation value
      const { error: translationError } = await supabase
        .from('translation_defaults')
        .update({
          default_value: category.name
        })
        .eq('key', category.translation_key);
      
      if (translationError) throw translationError;
      
      // Then update the category itself
      const { error } = await supabase
        .from('categories')
        .update({
          description: category.description,
          category_group_id: category.category_group_id,
          business_banner_image_url: category.business_banner_image_url
        })
        .eq('id', category.id);
      
      if (error) throw error;
      
      return true;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminCategories'] });
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      toast({
        title: "Success",
        description: "Category updated successfully",
      });
      setIsEditingCategory(false);
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to update category",
        variant: "destructive",
      });
      console.error("Error updating category:", error);
    },
  });

  // Delete a category
  const deleteCategoryMutation = useMutation({
    mutationFn: async (id: string) => {
      // Get the translation key first
      const { data, error: fetchError } = await supabase
        .from('categories')
        .select('translation_key')
        .eq('id', id)
        .single();
      
      if (fetchError) throw fetchError;
      
      // Delete the category
      const { error } = await supabase
        .from('categories')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      // Delete the translation defaults entry - this will fail if it's in use elsewhere
      // So we wrap it in a try/catch
      try {
        await supabase
          .from('translation_defaults')
          .delete()
          .eq('key', data.translation_key);
      } catch (error) {
        console.log('Translation key still in use by other records, not deleting from defaults');
      }
      
      return true;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminCategories'] });
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      toast({
        title: "Success",
        description: "Category deleted successfully",
      });
      setIsDeletingCategory(false);
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to delete category",
        variant: "destructive",
      });
      console.error("Error deleting category:", error);
    },
  });

  const handleEditCategory = (category: { id: string; name: string; description: string; category_group_id: string; translation_key: string; business_banner_image_url: string }) => {
    setEditingCategory({ ...category });
    setIsEditingCategory(true);
  };

  const handleUpdateCategory = async () => {
    if (!editingCategory.name.trim() || !editingCategory.category_group_id) {
      toast({
        title: "Error",
        description: "Category name and group are required",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsUpdatingCategory(true);
      await updateCategoryMutation.mutateAsync(editingCategory);
    } catch (error) {
      console.error("Error updating category:", error);
    } finally {
      setIsUpdatingCategory(false);
    }
  };

  const handleDeleteCategory = (id: string) => {
    setCategoryToDelete(id);
    setIsDeletingCategory(true);
  };

  const confirmDeleteCategory = async () => {
    try {
      setIsDeleting(true);
      await deleteCategoryMutation.mutateAsync(categoryToDelete);
    } catch (error) {
      console.error("Error deleting category:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const cancelDeleteCategory = () => {
    setIsDeletingCategory(false);
    setCategoryToDelete("");
  };

  // Update category group icon
  const updateCategoryGroupIconMutation = useMutation({
    mutationFn: async ({ id, icon_url }: { id: string; icon_url: string }) => {
      const { error } = await supabase
        .from('category_groups')
        .update({ icon_url })
        .eq('id', id);

      if (error) throw error;
      return true;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminCategoryGroups'] });
      queryClient.invalidateQueries({ queryKey: ['categoryGroups'] });
      toast({
        title: "Success",
        description: "Category group icon updated successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to update category group icon",
        variant: "destructive",
      });
      console.error("Error updating category group icon:", error);
    },
  });

  const handleSelectIcon = (categoryGroup: { id: string; name: string; translation_key: string }) => {
    setSelectedCategoryGroupForIcon(categoryGroup);
    setIsIconSearchOpen(true);
  };

  const handleIconSelected = async (iconUrl: string) => {
    if (!selectedCategoryGroupForIcon) return;

    try {
      await updateCategoryGroupIconMutation.mutateAsync({
        id: selectedCategoryGroupForIcon.id,
        icon_url: iconUrl,
      });
      setIsIconSearchOpen(false);
      setSelectedCategoryGroupForIcon(null);
    } catch (error) {
      console.error("Error updating icon:", error);
    }
  };

  // Add a category group with translation support
  const addCategoryGroup = async () => {
    if (!newCategoryGroup.name.trim()) {
      toast({
        title: "Error",
        description: "Category group name is required",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsAddingGroup(true);
      
      // Generate translation key
      const translationKey = generateTranslationKey('category_group', newCategoryGroup.name);
      
      // First, insert into translation_defaults
      const { data: translationData, error: translationError } = await supabase
        .from('translation_defaults')
        .insert([{
          key: translationKey,
          default_value: newCategoryGroup.name,
          description: `Category group name: ${newCategoryGroup.name}`
        }])
        .select();
      
      if (translationError) {
        if (translationError.code === '23505') { // Unique violation error code
          toast({
            title: "Error",
            description: "A translation key for this name already exists. Please use a different name.",
            variant: "destructive",
          });
        } else {
          throw translationError;
        }
        return;
      }
      
      // Then, insert into category_groups
      const { data, error } = await supabase
        .from('category_groups')
        .insert([{
          description: newCategoryGroup.description,
          translation_key: translationKey
        }])
        .select();

      if (error) throw error;

      toast({
        title: "Success",
        description: "Category group added successfully",
      });
      
      setNewCategoryGroup({ name: "", description: "" });
      setIsAddingCategoryGroup(false);
      queryClient.invalidateQueries({ queryKey: ['adminCategoryGroups'] });
      queryClient.invalidateQueries({ queryKey: ['categoryGroups'] });
    } catch (error) {
      console.error("Error adding category group:", error);
      toast({
        title: "Error",
        description: "Failed to add category group",
        variant: "destructive",
      });
    } finally {
      setIsAddingGroup(false);
    }
  };

  // Add a category with translation support
  const addCategory = async () => {
    if (!newCategory.name.trim() || !newCategory.category_group_id) {
      toast({
        title: "Error",
        description: "Category name and group are required",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsAddingCategory(true);
      
      // Generate translation key
      const translationKey = generateTranslationKey('category', newCategory.name);
      
      // First, insert into translation_defaults
      const { data: translationData, error: translationError } = await supabase
        .from('translation_defaults')
        .insert([{
          key: translationKey,
          default_value: newCategory.name,
          description: `Category name: ${newCategory.name}`
        }])
        .select();
      
      if (translationError) {
        if (translationError.code === '23505') { // Unique violation error code
          toast({
            title: "Error",
            description: "A translation key for this name already exists. Please use a different name.",
            variant: "destructive",
          });
        } else {
          throw translationError;
        }
        return;
      }
      
      // Then, insert into categories
      const { data, error } = await supabase
        .from('categories')
        .insert([{
          description: newCategory.description,
          category_group_id: newCategory.category_group_id,
          translation_key: translationKey,
          business_banner_image_url: newCategory.business_banner_image_url
        }])
        .select();

      if (error) throw error;

      toast({
        title: "Success",
        description: "Category added successfully",
      });
      
      setNewCategory({ name: "", description: "", category_group_id: "", business_banner_image_url: "" });
      setIsAddingCategoryDialog(false);
      queryClient.invalidateQueries({ queryKey: ['adminCategories'] });
      queryClient.invalidateQueries({ queryKey: ['categories'] });
    } catch (error) {
      console.error("Error adding category:", error);
      toast({
        title: "Error",
        description: "Failed to add category",
        variant: "destructive",
      });
    } finally {
      setIsAddingCategory(false);
    }
  };

  return (
    <div className="space-y-6 sm:space-y-8 animate-fade-in">
      <div className="relative">
        <h1 className="admin-section-title mb-2">Category Management</h1>
        <p className="text-muted-foreground/80 text-lg">
          Organize your business categories and groups for better navigation
        </p>
      </div>
      
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-800">Category Groups</h2>
          <p className="text-sm text-muted-foreground/80">Manage your category groups here</p>
        </div>
        <Button onClick={() => setIsAddingCategoryGroup(true)} className="gap-2 rounded-lg  px-6 py-2.5 w-full sm:w-auto">
          <Plus className="h-4 w-4" />
          <span className="hidden sm:inline">Add Category Group</span>
          <span className="sm:hidden">Add Group</span>
        </Button>
      </div>

      <Dialog open={isAddingCategoryGroup} onOpenChange={setIsAddingCategoryGroup}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Add Category Group</DialogTitle>
            <DialogDescription>
              Add a new category group to organize your categories.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="name" className="text-right">
                Name
              </Label>
              <Input
                id="name"
                value={newCategoryGroup.name}
                onChange={(e) => setNewCategoryGroup({ ...newCategoryGroup, name: e.target.value })}
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="description" className="text-right">
                Description
              </Label>
              <Textarea
                id="description"
                value={newCategoryGroup.description}
                onChange={(e) => setNewCategoryGroup({ ...newCategoryGroup, description: e.target.value })}
                className="col-span-3"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => setIsAddingCategoryGroup(false)}>
              Cancel
            </Button>
            <Button type="submit" onClick={addCategoryGroup} disabled={isAddingGroup}>
              {isAddingGroup && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Add Group
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card className="admin-card admin-card-stats">
        <CardContent className="p-6">
          {categoryGroupsLoading ? (
            <div className="flex justify-center py-12">
              <div className="flex space-x-2">
                <div className="h-3 w-3 bg-gradient-to-r from-primary to-accent rounded-full animate-pulse"></div>
                <div className="h-3 w-3 bg-gradient-to-r from-accent to-primary rounded-full animate-pulse animation-delay-200"></div>
                <div className="h-3 w-3 bg-gradient-to-r from-primary to-accent rounded-full animate-pulse animation-delay-400"></div>
              </div>
            </div>
          ) : isMobile ? (
            // Mobile card view for category groups
            <div className="space-y-4">
              {categoryGroupsData?.map((group, index) => (
                <Card key={group.id} className={`admin-card admin-card-action animate-fade-in stagger-${Math.min(index + 1, 5)} `}>
                  <CardContent className="pt-6">
                    <div className="space-y-3">
                      <div className="flex gap-4">
                        <div className="flex-shrink-0">
                          {group.icon_url ? (
                            <img
                              src={group.icon_url}
                              alt={`${group.name} icon`}
                              className="w-12 h-12 object-contain"
                              loading="lazy"
                            />
                          ) : (
                            <div className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center">
                              <ImageIcon className="h-6 w-6 text-gray-400" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-slate-800">{group.name}</h3>
                          <p className="text-xs text-muted-foreground/70 mt-1">{group.translation_key}</p>
                        </div>
                      </div>
                      <p className="text-sm text-slate-700">{group.description}</p>
                      <div className="pt-2 border-t border-gray-100">
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full"
                          onClick={() => handleSelectIcon({ id: group.id, name: group.name, translation_key: group.translation_key })}
                        >
                          <ImageIcon className="h-4 w-4 mr-2" />
                          {group.icon_url ? 'Change Icon' : 'Select Icon'}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            // Desktop table view for category groups
            <div className="rounded-xl border border-gray-200/60 overflow-hidden bg-white shadow-sm">
              <Table>
                <TableHeader>
                  <TableRow className="border-b border-gray-100 bg-gray-50/50">
                    <TableHead className="font-semibold text-slate-700 h-12 w-16">Icon</TableHead>
                    <TableHead className="font-semibold text-slate-700">Name</TableHead>
                    <TableHead className="font-semibold text-slate-700">Translation Key</TableHead>
                    <TableHead className="font-semibold text-slate-700">Description</TableHead>
                    <TableHead className="text-right font-semibold text-slate-700">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {categoryGroupsData?.map((group) => (
                    <TableRow key={group.id} className="hover:bg-gray-50/80 border-b border-gray-100/50 transition-colors">
                      <TableCell className="w-16">
                        {group.icon_url ? (
                          <img
                            src={group.icon_url}
                            alt={`${group.name} icon`}
                            className="w-10 h-10 object-contain"
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center">
                            <ImageIcon className="h-5 w-5 text-gray-400" />
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="font-medium">{group.name}</TableCell>
                      <TableCell className="text-muted-foreground/80">{group.translation_key}</TableCell>
                      <TableCell>{group.description}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8"
                          onClick={() => handleSelectIcon({ id: group.id, name: group.name, translation_key: group.translation_key })}
                        >
                          <ImageIcon className="h-4 w-4 mr-2" />
                          {group.icon_url ? 'Change Icon' : 'Select Icon'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-800">Categories</h2>
          <p className="text-sm text-muted-foreground/80">Manage existing categories</p>
        </div>
        <Dialog>
          <DialogTrigger asChild>
            <Button onClick={() => setIsAddingCategoryDialog(true)} className="gap-2 rounded-lg  px-6 py-2.5 w-full sm:w-auto">
              <Plus className="h-4 w-4" />
              Add Category
            </Button>
          </DialogTrigger>
        </Dialog>
      </div>

      <Dialog open={isAddingCategoryDialog} onOpenChange={setIsAddingCategoryDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Add Category</DialogTitle>
            <DialogDescription>
              Add a new category to your platform.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="name" className="text-right">
                Name
              </Label>
              <Input
                id="name"
                value={newCategory.name}
                onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="description" className="text-right">
                Description
              </Label>
              <Textarea
                id="description"
                value={newCategory.description}
                onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="category_group" className="text-right">
                Category Group
              </Label>
              <Select onValueChange={(value) => setNewCategory({ ...newCategory, category_group_id: value })}>
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder="Select a category group" />
                </SelectTrigger>
                <SelectContent>
                  {categoryGroupsData?.map((group) => (
                    <SelectItem key={group.id} value={group.id}>
                      {group.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-4 items-start gap-4">
              <Label htmlFor="banner_image" className="text-right pt-2">
                Banner Image
              </Label>
              <div className="col-span-3 space-y-2 min-w-0">
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button 
                    type="button" 
                    variant="outline"
                    onClick={() => setIsImageSearchOpen(true)}
                    className="flex-1 min-w-0 text-xs sm:text-sm px-2 sm:px-4"
                  >
                    <ImageIcon className="h-4 w-4 mr-1 sm:mr-2 flex-shrink-0" />
                    <span className="truncate">Search Images</span>
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline"
                    onClick={() => setIsImageUploadOpen(true)}
                    className="flex-1 min-w-0 text-xs sm:text-sm px-2 sm:px-4"
                  >
                    <Upload className="h-4 w-4 mr-1 sm:mr-2 flex-shrink-0" />
                    <span className="truncate">Upload Image</span>
                  </Button>
                </div>
                {newCategory.business_banner_image_url && (
                  <div className="relative">
                    <img 
                      src={newCategory.business_banner_image_url} 
                      alt="Banner preview" 
                      className="w-full h-20 object-cover rounded border"
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      className="absolute top-1 right-1 h-6 w-6 p-0"
                      onClick={() => setNewCategory({ ...newCategory, business_banner_image_url: "" })}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => setIsAddingCategoryDialog(false)}>
              Cancel
            </Button>
            <Button type="submit" onClick={addCategory} disabled={isAddingCategory}>
              {isAddingCategory && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Add Category
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card className="admin-card admin-card-stats">
        <CardContent className="p-6">
          {categoriesLoading ? (
            <div className="flex justify-center py-12">
              <div className="flex space-x-2">
                <div className="h-3 w-3 bg-gradient-to-r from-primary to-accent rounded-full animate-pulse"></div>
                <div className="h-3 w-3 bg-gradient-to-r from-accent to-primary rounded-full animate-pulse animation-delay-200"></div>
                <div className="h-3 w-3 bg-gradient-to-r from-primary to-accent rounded-full animate-pulse animation-delay-400"></div>
              </div>
            </div>
          ) : isMobile ? (
            // Mobile card view for categories
            <div className="space-y-4">
              {categoriesData?.map((category, index) => {
                const categoryGroup = categoryGroupsData?.find((group) => group.id === category.category_group_id);
                return (
                  <Card key={category.id} className={`admin-card admin-card-action animate-fade-in stagger-${Math.min(index + 1, 5)} `}>
                    <CardContent className="pt-6">
                      <div className="space-y-4">
                        <div className="flex gap-4">
                          <div className="flex-1">
                            <h3 className="font-semibold text-slate-800 break-words">{category.name}</h3>
                            <p className="text-xs text-muted-foreground/70 mt-1 break-all">{category.translation_key}</p>
                          </div>
                          {category.business_banner_image_url && (
                            <div className="flex-shrink-0">
                              <img 
                                src={category.business_banner_image_url} 
                                alt={`${category.name} banner`}
                                className="w-20 h-14 object-cover rounded border border-gray-200"
                                loading="lazy"
                              />
                            </div>
                          )}
                        </div>
                        <div>
                          <p className="text-sm text-slate-700 mb-2">{category.description}</p>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground/80">Group:</span>
                            <span className="text-xs font-medium text-slate-600">{categoryGroup?.name || "N/A"}</span>
                          </div>
                        </div>
                        <div className="flex justify-end gap-2 pt-3 border-t border-gray-100" onClick={(e) => e.stopPropagation()}>
                          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => handleEditCategory(category)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="outline" size="icon" className="h-8 w-8 text-red-600 border-red-300 hover:bg-red-50 hover:border-red-400" onClick={() => handleDeleteCategory(category.id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            // Desktop table view for categories
            <div className="rounded-xl border border-gray-200/60 overflow-hidden bg-white shadow-sm">
              <Table>
                <TableHeader>
                  <TableRow className="border-b border-gray-100 bg-gray-50/50">
                    <TableHead className="font-semibold text-slate-700 h-12">Name</TableHead>
                    <TableHead className="font-semibold text-slate-700">Translation Key</TableHead>
                    <TableHead className="font-semibold text-slate-700">Description</TableHead>
                    <TableHead className="font-semibold text-slate-700">Business Banner</TableHead>
                    <TableHead className="font-semibold text-slate-700">Category Group</TableHead>
                    <TableHead className="text-right font-semibold text-slate-700">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {categoriesData?.map((category) => {
                    const categoryGroup = categoryGroupsData?.find((group) => group.id === category.category_group_id);
                    return (
                      <TableRow key={category.id} className="hover:bg-gray-50/80 border-b border-gray-100/50 transition-colors">
                        <TableCell className="font-medium">{category.name}</TableCell>
                        <TableCell className="text-muted-foreground/80">{category.translation_key}</TableCell>
                        <TableCell>{category.description}</TableCell>
                        <TableCell className="w-24">
                          {category.business_banner_image_url && (
                            <img 
                              src={category.business_banner_image_url} 
                              alt={`${category.name} banner`}
                              className="w-20 h-12 object-cover rounded border border-gray-200"
                              loading="lazy"
                            />
                          )}
                        </TableCell>
                        <TableCell>{categoryGroup?.name || "N/A"}</TableCell>
                        <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                          <div className="flex justify-end gap-2">
                            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => handleEditCategory(category)}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button variant="outline" size="icon" className="h-8 w-8 text-red-600 border-red-300 hover:bg-red-50 hover:border-red-400" onClick={() => handleDeleteCategory(category.id)}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={isEditingCategory} onOpenChange={setIsEditingCategory}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Edit Category</DialogTitle>
            <DialogDescription>
              Edit the details of the selected category.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="name" className="text-right">
                Name
              </Label>
              <Input
                id="name"
                value={editingCategory.name}
                onChange={(e) => setEditingCategory({ ...editingCategory, name: e.target.value })}
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="translation_key" className="text-right">
                Translation Key
              </Label>
              <Input
                id="translation_key"
                value={editingCategory.translation_key}
                readOnly
                className="col-span-3 bg-muted"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="description" className="text-right">
                Description
              </Label>
              <Textarea
                id="description"
                value={editingCategory.description}
                onChange={(e) => setEditingCategory({ ...editingCategory, description: e.target.value })}
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="category_group" className="text-right">
                Category Group
              </Label>
              <Select
                value={editingCategory.category_group_id}
                onValueChange={(value) => setEditingCategory({ ...editingCategory, category_group_id: value })}
              >
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder="Select a category group" />
                </SelectTrigger>
                <SelectContent>
                  {categoryGroupsData?.map((group) => (
                    <SelectItem key={group.id} value={group.id}>
                      {group.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-4 items-start gap-4">
              <Label htmlFor="edit_banner_image" className="text-right pt-2">
                Banner Image
              </Label>
              <div className="col-span-3 space-y-2 min-w-0">
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button 
                    type="button" 
                    variant="outline"
                    onClick={() => setIsEditImageSearchOpen(true)}
                    className="flex-1 min-w-0 text-xs sm:text-sm px-2 sm:px-4"
                  >
                    <ImageIcon className="h-4 w-4 mr-1 sm:mr-2 flex-shrink-0" />
                    <span className="truncate">Search Images</span>
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline"
                    onClick={() => setIsEditImageUploadOpen(true)}
                    className="flex-1 min-w-0 text-xs sm:text-sm px-2 sm:px-4"
                  >
                    <Upload className="h-4 w-4 mr-1 sm:mr-2 flex-shrink-0" />
                    <span className="truncate">Upload Image</span>
                  </Button>
                </div>
                {editingCategory.business_banner_image_url && (
                  <div className="relative">
                    <img 
                      src={editingCategory.business_banner_image_url} 
                      alt="Banner preview" 
                      className="w-full h-20 object-cover rounded border"
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      className="absolute top-1 right-1 h-6 w-6 p-0"
                      onClick={() => setEditingCategory({ ...editingCategory, business_banner_image_url: "" })}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => setIsEditingCategory(false)}>
              Cancel
            </Button>
            <Button type="submit" onClick={handleUpdateCategory} disabled={isUpdatingCategory}>
              {isUpdatingCategory && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Update Category
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isDeletingCategory} onOpenChange={setIsDeletingCategory}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Delete Category</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this category? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={cancelDeleteCategory}>
              Cancel
            </Button>
            <Button type="submit" variant="destructive" onClick={confirmDeleteCategory} disabled={isDeleting}>
              {isDeleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image Search Modals */}
      <ImageSearchModal
        isOpen={isImageSearchOpen}
        onClose={() => setIsImageSearchOpen(false)}
        onSelectImage={(imageUrl) => {
          setNewCategory({ ...newCategory, business_banner_image_url: imageUrl });
          setIsImageSearchOpen(false);
        }}
        initialQuery={newCategory.name}
      />

      <ImageSearchModal
        isOpen={isEditImageSearchOpen}
        onClose={() => setIsEditImageSearchOpen(false)}
        onSelectImage={(imageUrl) => {
          setEditingCategory({ ...editingCategory, business_banner_image_url: imageUrl });
          setIsEditImageSearchOpen(false);
        }}
        initialQuery={editingCategory.name}
      />

      {/* Image Upload Modals */}
      <ImageUploadModal
        isOpen={isImageUploadOpen}
        onClose={() => setIsImageUploadOpen(false)}
        onSelectImage={(imageUrl) => {
          setNewCategory({ ...newCategory, business_banner_image_url: imageUrl });
          setIsImageUploadOpen(false);
        }}
        categoryName={newCategory.name}
      />

      <ImageUploadModal
        isOpen={isEditImageUploadOpen}
        onClose={() => setIsEditImageUploadOpen(false)}
        onSelectImage={(imageUrl) => {
          setEditingCategory({ ...editingCategory, business_banner_image_url: imageUrl });
          setIsEditImageUploadOpen(false);
        }}
        categoryName={editingCategory.name}
      />

      {/* Freepik Icon Search Modal */}
      <FreepikIconSearchModal
        isOpen={isIconSearchOpen}
        onClose={() => {
          setIsIconSearchOpen(false);
          setSelectedCategoryGroupForIcon(null);
        }}
        onSelectIcon={handleIconSelected}
        initialQuery={selectedCategoryGroupForIcon?.name || ''}
        categoryGroupSlug={selectedCategoryGroupForIcon?.translation_key.replace('category_group.', '') || ''}
      />
    </div>
  );
};

export default CategoryManagement;
